import Chart from './Chart';
import Tooltip from './Tooltip';
import * as d3 from 'd3';

export default class Line extends Chart {
  constructor(svg, height, width, margin, trnsTime, tooltipDiv) {
    super(svg, height, width, margin, trnsTime);

    this.tooltip = new Tooltip(tooltipDiv);
    this._initializeHelperFunctions();
    this._initializeChart();
  }

  _initializeHelperFunctions() {
    this.dateParse = d3.timeParse('%Y');

    this.xPosition = d3.scaleTime()
      .domain([0, 1])
      .range([0, this.width - this.margin.left - this.margin.right]);

    this.yPosition = d3.scaleLinear()
      .domain([0, 1])
      .range([0, this.height - this.margin.top - this.margin.bottom]);
  }

  _initializeChart() {
    // Make SVG Responsive
    this.svg
      .attr('viewBox', `0 0 ${this.width} ${this.height}`)
      .attr('preserveAspectRatio' , 'xMinYMin meet');

    // X-Axis Group
    this.xAxis = this.svg.append('g')
      .attr('transform', 
        `translate(${this.margin.left},${this.height - this.margin.bottom})`)
      .call(d3.axisBottom(this.xPosition));

    // X-Axis Label
    this.xLabel = this.svg.append('g')
      .append('text')
        .attr('transform', 
          `translate(${(this.width - this.margin.right + this.margin.left) / 2},
          ${this.height - 2})`)
        .style('text-anchor', 'middle')
        .text('Year');
    
    // Y-Axis Group
    this.yAxis = this.svg.append('g')
      .attr('transform', `translate(${this.margin.left},${this.margin.top})`)
      .call(d3.axisLeft(this.yPosition).tickSize(0));

    // Y-Axis Label
    this.yLabel = this.svg.append('g')
      .append('text')
        .attr('transform', `translate(${this.margin.left * 0.3},
          ${this.height / 2})rotate(${-90})`)
        .style('text-anchor', 'middle')
        .text(this.units);
    
    // Lines Group
    this.lines = this.svg.append('g')
      .attr('transform', `translate(${this.margin.left},${this.margin.top})`);

    this.points = this.svg.append('g')
      .attr('transform', `translate(${this.margin.left},${this.margin.top})`);

    // Legend Group
    this.legend = this.svg.append('g')
      .attr('transform', `translate(${this.width - this.margin.right + 
        this.margin.left * 1.5},${this.margin.top+10})`);

    // Events Group - Layer ontop of the plot to draw components on
    this.plotEvents = this.svg.append('g')
      .attr('transform', `translate(${this.margin.left},${this.margin.top})`);

    // Events Group - Vertical Line indicating mouse position
    this.plotEvents
      .append('path')
        .attr('stroke', 'black')
        .attr('stroke-width', 1)
        .style('opacity', 0);

    // Events Group - SVG to trigger events
    this.plotEvents
      .append('svg:rect')
        .attr('width', this.width - this.margin.right)
        .attr('height', this.height - this.margin.top)
        .attr('pointer-events', 'all')
        .attr('fill', 'none');

    // Default Mouse Events Below
    this.setOnPlotMouseOver();
    this.setOnPlotMouseLeave();
    this.setOnPlotMouseMove();
  }

  drawChart() {
    this.color.domain(this.data.map(d => d.key).sort(d3.ascending));

    this._drawXaxis();
    this._drawYaxis();
    this._drawLines();
    this._drawPoints();
    this._drawLegend();
    this._drawPlotEvents();
  }

  _drawXaxis() {
    this.xPosition
      .domain([d3.min(this.data, d => d.minYear),
        d3.max(this.data, d => d.maxYear)]);

    this.xAxis
      .transition()
      .duration(this.transitionTime)
        .call(
          d3.axisBottom(this.xPosition)
            .ticks(d3.timeYear.every(1))
            .tickFormat(d3.timeFormat('%Y'))
          );

    this.xAxis
      .selectAll('path')
        .attr('color', 'gray');
  }

  _drawYaxis() {
    this.yPosition
      .domain([d3.max(this.data, d => d3.max(d.value, d => d.value)), 0.1])
      .nice();

    this.yAxis
      .call(
        d3.axisLeft(this.yPosition)
          .tickSize(-this.width + this.margin.right + this.margin.left)
      )
      .selectAll('path')
        // .attr('color', 'gray')
        .style('opacity', 0);

    this.yAxis
      .selectAll('line')
        .style('opacity', 0.15)

    this.yLabel
      .text(this.units);
  }

  _drawLines() {
    this.lines.selectAll('*').remove();

    const calcLines = d3.line()
      .x(d => this.xPosition(this.dateParse(d.year)))
      .y(d => this.yPosition(d.value));

    this.lines
      .selectAll('path')
      .data(this.data)
      .enter()
      .append('path')
        .attr('d', d => calcLines(d.value))
        .attr('fill', 'none')
        .attr('stroke', d => this.color(d.key))
        .attr('stroke-width', 4)
        .style('opacity', 0.6)
        .attr('stroke-dasharray', function() { return this.getTotalLength(); })
        .attr('stroke-dashoffset', function() { return this.getTotalLength(); })
      .transition()
      .duration(this.transitionTime)
        .attr('stroke-dashoffset', 1);
  }

  _drawPoints() {
    this.points.selectAll('*').remove();

    this.points
      .selectAll('g')
      .data(this.data)
      .enter()
      .append('g')
        .attr('fill', d => this.color(d.key))
      .selectAll('circle')
      .data(d => d.value)
      .enter()
      .append('circle')
        .attr('cx', d => this.xPosition(this.dateParse(d.year)))
        .attr('cy', d => this.yPosition(d.value))
        .attr('r', 5)
        .attr('stroke-width', 3)
        .attr('stroke', 'white');
  }

  _drawLegend() {
    this.legend.selectAll('*').remove();

    // Legend Positioning and Text
    this.legend
      .selectAll('text')
      .data(this.data)
      .enter()
      .append('text')
        .attr('transform', (d, i) => `translate(${0},${i * 20})`);

    // Legend Color References
    this.legend
      .selectAll('circle')
      .data(this.data)
      .enter()
      .append('circle')
        .attr('r', '0.4em')
        .attr('cx', -5)
        .attr('cy', -5)
        .attr('transform', (d, i) => `translate(${-12},${i * 20})`)
        .attr('fill', d => this.color(d.key))
        .style('opacity', 0.8);

    this.legend
      .selectAll('circle, text')
        .on('mouseover', (d) => {
          this.tooltip.show(1);
          this.tooltip.changeContent(d.key);
        })
        .on('mouseleave', () => {
          this.tooltip.hide();
        })
        .on('mousemove', () => {
          this.tooltip.move(d3.event.pageX + 10, d3.event.pageY - 25);
        });
  }

  _drawPlotEvents() {
    this.plotEvents.selectAll('g').remove();

    // Circle and Text Elements for Plot Events
    const plotMarkers = this.plotEvents.selectAll('g')
      .data(this.data)
      .enter()
        .append('g')
        .attr('pointer-events', 'none');

    plotMarkers
      .append('circle')
        .attr("r", 7)
        .style("stroke", d => this.color(d.key))
        .style("fill", "none")
        .style("stroke-width", 1)
        .style("opacity", 0);

    plotMarkers
      .append("text")
        .attr("transform", "translate(10,3)");

    // Set Event Listeners on Transparent SVG Rectangle over the Plot
    this.plotEvents
      .select('rect')
      .on('mouseover', this.handleMouseOver)
      .on('mousemove', this.handleMouseMove)
      .on('mouseleave', this.handleMouseLeave);
  }

  toLogScale() {
    let min = d3.min(this.data, d => d3.min(d.value, d => d.value));
    min = min <= 0 ? 0 : min;

    this.yPosition = d3.scaleLog()
      .domain([d3.max(this.data, d => d3.max(d.value, d => d.value)), min])
      .nice()
      .range([0, this.height - this.margin.top - this.margin.bottom]);

    this.yAxis
      .transition()
      .duration(this.transitionTime)
      .call(
        d3.axisLeft(this.yPosition)
          .tickSize(-this.width + this.margin.right + this.margin.left)
          .ticks(null, d3.format(','))
      )
      .selectAll('line')
        .style('opacity', 0.15);

    const calcLines = d3.line()
      .x(d => this.xPosition(this.dateParse(d.year)))
      .y(d => this.yPosition(d.value));
    
    // Prevents the dash array to make the path appear too short when swapping
    // from linear to log
    this.lines.selectAll('path')
      .attr('stroke-dasharray', 0);

    this.lines
      .selectAll('path')
        .transition()
        .duration(this.transitionTime)
        .attr('d', d => calcLines(d.f_value || d.value))
    
    this.points
      .selectAll('circle')
        .transition()
        .duration(this.transitionTime)
        .attr('cy', d => this.yPosition(d.value));
  }

  toLinearScale() {
    this.yPosition = d3.scaleLinear()
      .domain([d3.max(this.data, d => d3.max(d.value, d => d.value)), 0])
      .nice()
      .range([0, this.height - this.margin.top - this.margin.bottom]);

    this.yAxis
      .transition()
      .duration(this.transitionTime)
        .call(
          d3.axisLeft(this.yPosition)
            .tickSize(-this.width + this.margin.right + this.margin.left)
        )
        .selectAll('line')
          .style('opacity', 0.15);
    
    const calcLines = d3.line()
      .x(d => this.xPosition(this.dateParse(d.year)))
      .y(d => this.yPosition(d.value));
    
    // Prevents the dash array to make the path appear too short when swapping
    // from linear to log
    this.lines.selectAll('path')
      .attr('stroke-dasharray', 0);

    this.lines
      .selectAll('path')
        .transition()
        .duration(this.transitionTime)
        .attr('d', d => calcLines(d.f_value || d.value));

    this.points
      .selectAll('circle')
        .transition()
        .duration(this.transitionTime)
        .attr('cy', d => this.yPosition(d.value));
  }

  setYearRange([min, max]) {
    // Can remove this once properly implemented  
    const data = this.data.map(key => {
      key.f_value = key.value.filter(val => {
        return val.year >= min && val.year <= max;
      });
      return key;
    });

    const newMax = d3.max(data, arr => d3.max(arr.f_value, d => d.value));

    this.xPosition.domain([min, max].map(d => this.dateParse(d)));
    this.xAxis
      .transition()
      .duration(this.transitionTime)
        .call(
          d3.axisBottom(this.xPosition)
            .ticks(d3.timeYear.every(1))
            .tickFormat(d3.timeFormat('%Y'))
        );
    
    this.yPosition.domain([newMax, 0.1]).nice();
    this.yAxis
      .transition()
      .duration(this.transitionTime)
        .call(
          d3.axisLeft(this.yPosition)
            .tickSize(-this.width + this.margin.right + this.margin.left)
        )
        .selectAll('line')
          .style('opacity', 0.15);

    this.lines.selectAll('path')
      .attr('stroke-dasharray', 0);

    const calcLines = d3.line()
      .x(d => this.xPosition(this.dateParse(d.year)))
      .y(d => this.yPosition(d.value));

    this.lines
      .selectAll('path')
      .data(data)
      .transition()
      .duration(this.transitionTime)
        .attr('d', d => calcLines(d.f_value));

    this.points
      .selectAll('circle')
      .transition()
      .duration(this.transitionTime)
        .attr('cx', d => this.xPosition(this.dateParse(d.year)))
        .attr('cy', d => this.yPosition(d.value))
        // Hide circles outside of chart range
        .attr('r', d => {
          const [leftLimit, rightLimit] = this.xPosition.range();
          const pos = this.xPosition(this.dateParse(d.year));
          return (pos >= leftLimit && pos <= rightLimit) ? 5 : 0;
        });
  }

  setData(data) {
    this.data = data.map(d => {
      [d.minYear, d.maxYear] = d3.extent(d.value, d => this.dateParse(d.year));
      return d;
    });
  }

  setOnPlotMouseMove(onMouseMove) {
    if (onMouseMove){
      this.handleMouseMove = onMouseMove;
    } else {
      // Default Event Handler
      this.handleMouseMove = () => {
        const dateFormat = d3.timeFormat('%Y'),
              [min, max] = this.xPosition.domain().map(d => dateFormat(d)),
              yearList = d3.range(min, parseInt(max) + 1)
                .map(d => this.dateParse(d)),
              mouseX = this.xPosition.invert(d3.mouse(d3.event.target)[0]),
              index = d3.bisectLeft(yearList, mouseX) - 1,
              xPos = this.xPosition(yearList[index]) + 0.5, 
                // 0.5 to match xAxis tick translation done by d3
              bisect = d3.bisector(d => this.dateParse(d.year)).left; 
  
        // Vertical Line Position
        this.plotEvents
          .select('path')
            .attr('d', `M${xPos} ${this.height - 
              this.margin.bottom - this.margin.bottom} ${xPos} ${0}`)
            .style('opacity', 0.4)
            .attr('stroke-dasharray', '0.5%');
        
        // Circle Indicator and Annotation Position
        this.plotEvents
          .selectAll('g')
            .attr('transform', (d, nodeIndex, nodes) => {
              const targetIndex = bisect(d.value, mouseX) - 1, 
                    i = targetIndex < 0 ? 0: targetIndex,
                    y = this.yPosition(d.value[i].value),
                    x = this.xPosition(this.dateParse(d.value[i].year));
  
              d3.select(nodes[nodeIndex])
                .select('text')
                  .text(d3.format(',.1f')(d.value[i].value));
                  
              return `translate(${x},${y})`;
            });
        
        const fontSize = this.plotEvents.select('text').style('font-size');

        this.plotEvents
          .selectAll('text')
            .attr('transform', `translate(${10},${-parseFloat(fontSize)})`)

        // Legend Value Update
        this.legend
          .selectAll('text')
            .text((d) => {
              const targetIndex = bisect(d.value, mouseX) - 1, 
                    i = targetIndex < 0 ? 0: targetIndex;
  
              return `${d3.format(',.1f')(d.value[i].value)}`;
            });
      }
    }
  }

  setOnPlotMouseOver(onMouseOver) {
    if (onMouseOver) {
      this.handleMouseOver = onMouseOver;
    } else {
      this.handleMouseLeave = () => {
        this.plotEvents
          .select('path')
            .style('opacity', 0)
  
        this.plotEvents
          .selectAll('circle')
            .style("opacity", 0);
  
        this.plotEvents
          .selectAll('text')
            .style('opacity', 0);
      };
    }
  }

  setOnPlotMouseLeave(onMouseLeave) {
    if (onMouseLeave) {
      this.handleMouseLeave = onMouseLeave;
    } else {
      this.handleMouseOver = () => {
        this.plotEvents
          .select('path')
            .style('opacity', 1)
  
        this.plotEvents
          .selectAll('circle')
            .style("opacity", 1);
  
        this.plotEvents
          .selectAll('text')
          .style('opacity', 1);
      };
    }
  }
}